/*
  SOLEIL — Floating Header Controller
  Structural plumbing only. The LOOK (border, font, colors) is applied entirely
  in soleil-custom.css, not here — this file is brand-neutral.

  Replaces Horizon's native sticky layer (disabled via enable_sticky_header:
  "never" in sections/header-group.json) with a floating island driven by the
  Soleil frontend stack (snippets/scripts.liquid):

    • Alpine  = UI state   — the discrete `mode` ('top' | 'revealed' | 'hidden')
                             and the data attributes / native solid styling it drives.
    • GSAP    = motion      — the translateY follow + hide/show write.
    • ScrollTrigger = sensor — reports scroll position + direction each update.

  Behaviour (unified, desktop + mobile):
    • At the very top      → header sits below the announcement bar and scrolls
                             with the page (mode 'top'); transparent over the hero
                             on transparent templates.
    • Scroll down mid-page → hidden (slides up off-screen).
    • Scroll up   mid-page → revealed in the regular (solid) scheme + a discreet
                             shadow, pinned to the top of the viewport.
    • Transparent is a top-of-page-only state.

  Loaded `defer` BEFORE alpine.min.js (document order) in snippets/scripts.liquid,
  so it registers its Alpine component on `alpine:init` before Alpine boots. Uses
  Soleil's single Alpine instance — no second Alpine, no double-boot.

  The fixed positioning + spacer are gated on `.soleil-header-ready` (added once
  measured) so there is no flash of a top:0 header before init; without JS the
  header stays in native flow. prefers-reduced-motion → state changes apply
  instantly (no tweens).
*/
(function () {
  'use strict';

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var SQUEEZE = window.matchMedia('(min-width: 990px)');
  function headerScroller() {
    return window;
  }

  var HIDE_CLEARANCE = 24; // extra px so the discreet shadow clears the viewport when hidden
  var COMMIT_THRESHOLD = 32; // px of SUSTAINED movement before flipping hide/reveal — kills
                             // momentum/trackpad jitter (tiny direction flips don't toggle).
                             // Raised 10→32 (2026-07-01): 10px let small trackpad direction-
                             // reversals mid-page flip hide↔reveal, each a 0.32s animated slide
                             // → the "rough" header flicker. 32px needs a deliberate gesture.
  var TOP_HYSTERESIS = 12; // dead-zone around the announcement boundary so crossing it
                           // doesn't rapid-toggle transparent↔solid (flicker)

  function registerHeader() {
    if (!window.Alpine) return;
    if (window.gsap && window.ScrollTrigger) gsap.registerPlugin(ScrollTrigger);

    Alpine.data('soleilHeader', function () {
      return {
        section: null, // the <header class="header-section"> wrapper (the element we move)
        spacer: null, // reserves the header's flow space once it goes position:fixed
        st: null, // the ScrollTrigger sensor (re-created when the scroller changes)
        annH: 0, // summed height of the header-group children above the header (announcement bar)
        headH: 0, // header section height
        hideOnScroll: true, // merchant setting — when false, header stays visible (never hides)
        revealShadow: true, // merchant setting — discreet shadow on the revealed (solid) state

        // ─── State: a scroll-derived `mode` + the `menuOpen` overlay ───────────
        mode: 'top', // 'top' | 'revealed' | 'hidden' (scroll only)
        menuOpen: false, // a mega-menu panel is open (hover/focus on a submenu trigger)
        lastY: 0,
        acc: 0,
        _rafPending: false,
        _lastKey: null, // last applied render key — guards against re-firing the tween every frame

        init: function () {
          var self = this;
          this.section = this.$el.closest('.header-section') || this.$el.parentElement;
          if (!this.section) return;

          // Freeze header transitions through boot (see soleil-custom.css
          // ".soleil-header-booting"): the first state application + any scroll-
          // restoration jump on a mid-page reload must snap into place, not animate
          // from a visible position. Dropped once the page has settled, below.
          document.documentElement.classList.add('soleil-header-booting');

          this.hideOnScroll = this.$el.dataset.soleilHideOnScroll !== 'false';
          this.revealShadow = this.$el.dataset.soleilRevealShadow !== 'false';
          if (this.revealShadow) this.section.classList.add('soleil-header--shadow');

          this.measure();

          this.spacer = document.createElement('div');
          this.spacer.className = 'soleil-header-spacer';
          this.section.insertAdjacentElement('afterend', this.spacer);
          this.applySpacer();

          this.setY(this.annH);

          this.bindScroll();

          // The scroller ELEMENT swaps when crossing 990px (window ↔ .page-wrapper);
          // a ScrollTrigger's scroller can't be mutated, so rebind on the breakpoint
          // change, then re-measure and re-sync position from the new scroller.
          SQUEEZE.addEventListener('change', function () {
            self.bindScroll();
            self.measure(); self.applySpacer();
            self.acc = 0; self._lastKey = null;
            var y = self.st ? self.st.scroll() : (window.scrollY || 0);
            self.lastY = y; // pre-seed so the first onScroll computes delta 0 (no spurious toggle)
            self.onScroll(y);
          });

          window.addEventListener('resize', function () { self.measure(); self.applySpacer(); }, { passive: true });

          // Mega-menu open = any submenu-trigger item hovered/focused. Re-evaluated
          // on a rAF so :hover/:focus-within has settled.
          var reeval = function () {
            if (self._rafPending) return;
            self._rafPending = true;
            requestAnimationFrame(function () { self._rafPending = false; self.setMenuOpen(self.detectMenuOpen()); });
          };
          // Detect on genuine pointer MOVEMENT (+ boundary exit + keyboard), NOT
          // pointerover/out. over/out fire every frame when the follow tween micro-
          // moves the fixed header under a STATIONARY pointer during a top-zone scroll
          // → a :has/:hover querySelector each frame (the subtle scroll-up stutter) and
          // spurious opens. pointermove only fires on real movement, so a stationary
          // hover-and-scroll runs zero detection.
          this.$el.addEventListener('pointermove', reeval, { passive: true });
          this.$el.addEventListener('pointerleave', reeval, { passive: true });
          this.$el.addEventListener('focusin', reeval);
          this.$el.addEventListener('focusout', reeval);

          // Seed + apply the initial state from the ACTUAL scroll offset — BEFORE adding
          // soleil-header-ready. Two reasons this order matters:
          //  1. A page can load already scrolled down (a flavor-switch reload restores
          //     scrollY mid-page); starting in the default 'top' would reveal the header
          //     then hide it on the first scroll update.
          //  2. The eased hide/reveal transition is gated on .soleil-header-ready (see
          //     soleil-custom.css). Applying the first state while NOT ready means it uses
          //     the base `transform 0s` transition, so the header snaps straight to hidden
          //     instead of animating down from a visible position — no "shows up then slides
          //     away" flicker. The pre-paint pre-hide (soleil-prescrolled, layout head) kept
          //     it off-screen through the deferred-script window; this hands over in place.
          // Only NOW flip on the ready flag (island look + eased transitions for real scroll).
          this.onScroll(window.scrollY || 0);
          document.documentElement.classList.add('soleil-header-ready');

          // Lift the boot freeze once the page has settled (load + a beat) — after any
          // scroll-restoration jump has landed — so real user-driven hide/reveal animates
          // normally from here on.
          var drop = function () { document.documentElement.classList.remove('soleil-header-booting'); };
          if (document.readyState === 'complete') { setTimeout(drop, 300); }
          else { window.addEventListener('load', function () { setTimeout(drop, 300); }, { once: true }); }
        },

        isTransparentTemplate: function () { return this.$el.hasAttribute('transparent'); },

        measure: function () {
          this.headH = this.section.offsetHeight || 0;
          var a = 0, n = this.section.previousElementSibling;
          while (n) { a += n.offsetHeight || 0; n = n.previousElementSibling; }
          this.annH = a;
        },

        applySpacer: function () {
          // Owner directive (2026-06-18): the floating header overlays the first
          // section on EVERY page — no reserved gap anywhere. Transparent templates
          // were already 0; regular templates now match (the at-top look goes solid
          // in applyState so the bar stays legible over real content).
          if (this.spacer) this.spacer.style.height = '0px';
        },

        // Create (or re-create) the scroll sensor bound to the CURRENT scroller.
        // Explicit `scroller` (not the inherited ScrollTrigger default) is what makes
        // the header read `.page-wrapper` on desktop regardless of load order (§5.1).
        bindScroll: function () {
          var self = this;
          if (this.st) { this.st.kill(); this.st = null; }
          if (window.ScrollTrigger) {
            this.st = ScrollTrigger.create({
              scroller: headerScroller(),
              onUpdate: function (s) { self.onScroll(s.scroll()); },
              onRefresh: function () { self.measure(); self.applySpacer(); },
            });
          } else if (!this._winBound) {
            // No ScrollTrigger (degraded): fall back to the window scroll event. Bound
            // once; on desktop the window won't move, but this path only runs if the
            // whole GSAP stack failed to load.
            this._winBound = true;
            window.addEventListener('scroll', function () { self.onScroll(window.scrollY || 0); }, { passive: true });
          }
        },

        setY: function (px) {
          // Direct transform write. The hide/reveal ANIMATION is a COMPOSITOR CSS
          // transition on the section (soleil-custom.css), gated by data-header-state,
          // so motion never runs on the main-thread rAF (no stop-motion under scroll/
          // touch contention). The top-zone follow uses the same write at 0s.
          this.section.style.transform = 'translateY(' + px + 'px)';
        },

        // will-change is a DYNAMIC hint, never persistent: a permanently-promoted
        // layer is cached as a low-res bitmap → the vector logo/icons look blurry on
        // retina. So promote the section only WHILE scrolling (smooth per-frame follow
        // + compositor hide/reveal), then drop the hint ~250ms after scroll stops so
        // the layer re-rasters at full resolution while it's stationary (= when read).
        bumpWillChange: function () {
          if (!this._wcOn) { this.section.style.willChange = 'transform'; this._wcOn = true; }
          var self = this;
          clearTimeout(this._wcTimer);
          this._wcTimer = setTimeout(function () {
            self.section.style.willChange = 'auto';
            self._wcOn = false;
          }, 250);
        },

        detectMenuOpen: function () {
          return !!this.$el.querySelector('.menu-list__list-item:is(:hover, :focus-within):has([aria-haspopup])');
        },
        setMenuOpen: function (v) { if (this.menuOpen !== v) { this.menuOpen = v; this.applyState(); } },

        onScroll: function (y) {
          this.bumpWillChange(); // promote only while scrolling; crisp when idle
          // 'top' zone, with LEAVE-side hysteresis: once at the top, stay 'top'
          // (transparent) until scrolled clearly past the announcement (annH + HYST)
          // — a dead-zone so crossing the boundary can't rapid-toggle. Entering 'top'
          // is at annH exactly, so the follow position stays continuous (no jump).
          var topBoundary = this.mode === 'top' ? this.annH + TOP_HYSTERESIS : this.annH;
          if (y <= topBoundary) {
            if (this.mode !== 'top') { this.mode = 'top'; this.acc = 0; }
            // Freeze the follow while a menu is open — the island must not creep under
            // the pointer mid-hover (motion + pointer churn), and the open panel stays
            // anchored to a stationary island.
            if (!this.menuOpen) this.setY(this.annH - Math.min(y, this.annH));
            this.lastY = y;
          } else {
            // Below the fold: direction-COMMIT hide/reveal (accumulator). Leaving
            // 'top' by scrolling DOWN never passes through 'revealed' — it stays 'top'
            // (transparent) until the hide commits, so it hides in one motion with no
            // transparent→solid flash. Tiny jitter (< threshold) never toggles.
            var delta = y - this.lastY;
            this.lastY = y;
            if (delta !== 0) {
              if ((delta > 0) !== (this.acc > 0)) this.acc = 0; // reversal → restart count
              this.acc += delta;
              if (this.acc >= COMMIT_THRESHOLD) { this.mode = this.hideOnScroll ? 'hidden' : 'revealed'; }
              else if (this.acc <= -COMMIT_THRESHOLD) { this.mode = 'revealed'; }
            }
          }
          this.applyState();
        },

        // ─── SINGLE SOURCE OF TRUTH — fixed priority, top to bottom ────────────
        //   1. menu open   → ALWAYS solid + visible (even at the very top).
        //   2. hidden      → off-screen (slide up, keep current look → no flash).
        //   3. mode 'top'  → transparent.
        //   4. otherwise   → solid (revealed).
        applyState: function () {
          // IDEMPOTENT: derive a render KEY; only act when it changes. applyState runs
          // every scroll frame, so without this the hide/reveal transition re-fires
          // every frame. The 'top' follow position is a cheap transform write in
          // onScroll (not a transition), so it stays smooth without re-running this.
          var key = this.menuOpen ? 'menu' : this.mode;
          if (key === this._lastKey) return;
          this._lastKey = key;

          // NB: set data-header-state BEFORE setY — the attr selects the CSS
          // transition (var(--soleil-motion-base) for hidden/solid), so it must be
          // live when the transform changes for the compositor transition to run.
          if (this.menuOpen) {
            this.$el.dataset.stickyState = 'active';
            this.section.dataset.headerState = 'solid';
            if (this.mode !== 'top') this.setY(0); // pinned; at top stay at the follow position
            return;
          }
          if (this.mode === 'top') {
            // Keep header-state 'transparent' = the 0s transform transition, so the
            // announcement-follow stays instant on EVERY page. The bar BACKGROUND is
            // the only difference: transparent templates stay see-through; regular
            // templates go SOLID (sticky-state) since the header now floats OVER the
            // first section (spacer 0) and a see-through bar over real content would
            // be illegible.
            this.section.dataset.headerState = 'transparent';
            if (this.isTransparentTemplate()) {
              this.$el.removeAttribute('data-sticky-state');
            } else {
              this.$el.dataset.stickyState = 'active';
            }
            // position via setY (follow) in onScroll
          } else if (this.mode === 'hidden') {
            this.section.dataset.headerState = 'hidden';
            this.setY(-(this.headH + HIDE_CLEARANCE)); // don't touch sticky-state → no flash
          } else {
            this.section.dataset.headerState = 'solid';
            this.$el.dataset.stickyState = 'active';
            this.setY(0);
          }
        },
      };
    });
  }

  document.addEventListener('alpine:init', registerHeader);
}());
